import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Card, Button, Typography, Skeleton, Divider } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import useShipments from "../../hooks/useShipments";
import ShipmentForm from "../../components/shipments/ShipmentForm";

const SectionHeaderSkeleton = ({ titleWidth = 160, subtitleWidth = 260 }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
    <Box>
      <Skeleton width={titleWidth} height={20} />
      <Skeleton width={subtitleWidth} height={16} />
    </Box>
  </Box>
);

const FieldSkeleton = (props) => (
  <Skeleton variant="rounded" height={56} sx={{ borderRadius: 1 }} {...props} />
);

// Mirrors ShipmentForm's actual edit-mode layout, same as
// CustomerEditShipment.jsx's skeleton.
const ShipmentFormSkeleton = () => (
  <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
    <SectionHeaderSkeleton titleWidth={150} subtitleWidth={280} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 120px" },
        gap: 2,
        mb: 2,
      }}
    >
      <FieldSkeleton />
      <FieldSkeleton />
      <FieldSkeleton />
    </Box>
    <FieldSkeleton />

    <Divider sx={{ my: 3 }} />

    <SectionHeaderSkeleton titleWidth={180} subtitleWidth={240} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
        gap: 2,
        mb: 2,
      }}
    >
      <FieldSkeleton />
      <FieldSkeleton />
      <FieldSkeleton />
    </Box>
    <Skeleton variant="rounded" height={80} sx={{ borderRadius: 1.5 }} />

    <Divider sx={{ my: 3 }} />

    <SectionHeaderSkeleton titleWidth={170} subtitleWidth={220} />
    {[0, 1].map((i) => (
      <Box
        key={i}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr 1fr auto" },
          gap: 2,
          mb: 1.5,
          alignItems: "center",
        }}
      >
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <FieldSkeleton height={48} />
        <Skeleton variant="circular" width={36} height={36} />
      </Box>
    ))}
    <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: 1, mt: 0.5 }} />

    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4 }}>
      <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" width={130} height={36} sx={{ borderRadius: 1 }} />
    </Box>
  </Box>
);

// The admin edit-shipment form, as its own page (/admin/shipments/:id/edit)
// instead of a modal — mirrors CustomerEditShipment.jsx exactly, but with
// the admin-only props (`isAdmin`) and the document-upload-on-save logic
// previously inlined in AdminShipmentDetail.jsx's now-removed edit dialog.
const AdminEditShipment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { current, loading, loadOne, update, clear } = useShipments();

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOne(id);
    return () => clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdate = async (data, filesMeta) => {
    setSaving(true);
    const result = await update(id, data);
    setSaving(false);
    if (result) {
      if (filesMeta?.pendingFiles?.length > 0) {
        try {
          let itemId;
          if (filesMeta.attachTo && filesMeta.attachTo.startsWith("item:")) {
            const idx = parseInt(filesMeta.attachTo.split(":")[1], 10);
            itemId = result.items?.[idx]?._id;
          }
          const documentService = (await import("../../services/documentService")).default;
          await documentService.upload(id, filesMeta.pendingFiles, filesMeta.docType, undefined, itemId);
        } catch (err) {
          enqueueSnackbar("Document upload failed (you can upload later)", { variant: "warning" });
        }
      }
      navigate(`/admin/shipments/${id}`);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(`/admin/shipments/${id}`))}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Edit Shipment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current?.trackingNumber ? `Tracking # ${current.trackingNumber}` : "Update this shipment's details"}
          </Typography>
        </Box>
      </Box>

      <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
        {loading && !current ? (
          <ShipmentFormSkeleton />
        ) : (
          <ShipmentForm
            asPage
            open
            onClose={() => navigate(`/admin/shipments/${id}`)}
            onSubmit={handleUpdate}
            initialData={current}
            loading={saving}
            isAdmin
          />
        )}
      </Card>
    </Box>
  );
};

export default AdminEditShipment;
